import { useState, useEffect, useMemo, useId } from 'react';
import { Checkbox, LoadingOverlay, cn } from '@skupreme/shared/ui';
import { HoverBorderButton, StyledButton } from '@elements/Button';
import { FieldRequiredLabel, MuteLabel } from '@elements/FormField';
import { FIELD_INPUT, FIELD_INPUT_INVALID } from '@elements/form-field/styles';
import { AppModal } from '@elements/Modal';
import { ErrorMessageConstant } from '@utility/constants/common';
import ShippingLabelsCarriersService from '@utility/services/shipping-labels-carriers';
import { ToastMessage } from '@utility/toast';
import { allMarketplaceRegionDropdownHook } from '@hooks/marketplace-region';
import { dropdownOptionsIncludeFlag } from '@elements/Flag';
import { CountryDropdownStyle } from '@elements/Dropdown';

type CredentialInputProps = {
  name: string;
  label: string;
  value: string;
  type: string;
  required?: boolean;
  invalid: boolean;
  onChange: (value: string) => void;
};

function CredentialInput({
  name,
  label,
  value,
  type,
  required,
  invalid,
  onChange,
}: CredentialInputProps) {
  const id = useId();
  const Label = required ? FieldRequiredLabel : MuteLabel;
  return (
    <div className="sk:mb-4 sk:flex sk:flex-col sk:gap-1">
      <Label htmlFor={id}>{label}</Label>
      <input
        id={id}
        name={name}
        type={type}
        placeholder={label}
        value={value}
        required={required}
        aria-invalid={invalid || undefined}
        onChange={event => onChange(event.target.value)}
        className={cn(FIELD_INPUT, invalid && FIELD_INPUT_INVALID)}
      />
    </div>
  );
}

export const DynamicFieldType = {
  text: 'text',
  password: 'password',
  checkbox: 'checkbox',
  dropdown: 'dropdown',
};

const field = (
  value,
  error,
  item,
  onChange,
  isConditional = false,
  dependsOn = '',
  countryData = [],
  countryLoading = false,
) => {
  switch (item.input_type) {
    case DynamicFieldType.text:
    case DynamicFieldType.password:
      return (
        <CredentialInput
          key={item.value}
          name={isConditional ? `${dependsOn}.${item.value}` : item.value}
          label={item.name}
          value={value}
          onChange={next =>
            onChange(
              isConditional ? `${dependsOn}.${item.value}` : item.value,
              next,
            )
          }
          required={item.required}
          type={item.input_type}
          invalid={!!error}
        />
      );
    case DynamicFieldType.checkbox:
      /* Uncontrolled, as Semantic's was: it starts unticked whatever the stored value. */
      return (
        <div key={item.value} className="sk:mb-4">
          <Checkbox
            name={isConditional ? `${dependsOn}.${item.value}` : item.value}
            value={value}
            onChange={checked =>
              onChange(
                isConditional ? `${dependsOn}.${item.value}` : item.value,
                checked,
              )
            }
            isRequired={item.required}
            isInvalid={!!error}
          >
            {item.name}
          </Checkbox>
        </div>
      );
    case DynamicFieldType.dropdown:
      switch (item?.list_name) {
        case 'country':
          return (
            <div
              key={item.value}
              className="sk:mb-4 sk:flex sk:flex-col sk:gap-1"
            >
              <FieldRequiredLabel>Country</FieldRequiredLabel>
              <CountryDropdownStyle
                loading={countryLoading}
                required={item.required}
                placeholder={item.name}
                labeled
                hint={item.name}
                defaultValue={value}
                error={!!error}
                search
                searchInput={{ autoComplete: 'none' }}
                selectOnBlur={false}
                selection
                options={dropdownOptionsIncludeFlag(
                  /* @ts-expect-error - TS2698: Spread types may only be created from object types. */
                  countryData?.map(item => ({ ...item, value: item.name })),
                )}
                onChange={(e, dropdownItem) => {
                  onChange(
                    isConditional ? `${dependsOn}.${item.value}` : item.value,
                    dropdownItem.value,
                  );
                }}
                value={value}
              />
            </div>
          );
        default:
          return <>dropdown</>;
      }
    default:
      break;
  }
};

export const setFieldError = (
  fields,
  value,
  isConditional = false,
  dependsOn,
) => {
  // if (Object.keys(value || {}).length === 0) {
  //     return {}
  // }
  const error = {};
  fields.forEach(item => {
    if (
      item.required &&
      !(
        value[isConditional ? `${dependsOn}.${item.value}` : item.value] || ''
      ).trim()
    ) {
      error[isConditional ? `${dependsOn}.${item.value}` : item.value] =
        `${item.value} is required`;
    }
  });
  return error;
};

function unflattenObject(flatObj) {
  const result = {};

  for (const flatKey in flatObj) {
    if (!Object.prototype.hasOwnProperty.call(flatObj, flatKey)) continue;
    const value = flatObj[flatKey];
    const keys = flatKey.split('.');
    let current = result;

    keys.forEach((key, index) => {
      // If it's the last key, assign the value
      if (index === keys.length - 1) {
        current[key] = value;
      } else {
        // Create the nested object if it doesn't exist
        if (!current[key] || typeof current[key] !== 'object') {
          current[key] = {};
        }
        current = current[key];
      }
    });
  }

  return result;
}

export function AddEditCredentialsPopup({
  open,
  closeModal,
  carrier,
  fulfillment_provider_id,
}) {
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({});
  const [fields, setFields] = useState([]);
  const [conditionalFields, setConditionalFields] = useState([]);
  const [submitted, setSubmitted] = useState(false);
  const { data: countryData, loading: countryLoading } =
    allMarketplaceRegionDropdownHook(true);

  useEffect(() => {
    setError('');
    if (open) {
      const formValue = {};
      carrier?.fields?.forEach(field => {
        const key = field?.value;
        if (carrier.existing_fields?.[key]?.value) {
          formValue[key] = carrier.existing_fields?.[key]?.value;
        }
      });
      // @ts-expect-error - TS2345: Argument of type 'any[]' is not assignable to parameter of type 'SetStateAction<...
      setFields([...carrier.fields]);
      setConditionalFields(
        // @ts-expect-error - TS2345: Argument of type 'any[]' is not assignable to parameter of type 'SetStateAction<...
        carrier.conditional_fields ? [...carrier.conditional_fields] : [],
      );
      setFormData({ ...formValue });
      setSubmitted(false);
    }
  }, [open]);

  const errors = useMemo(() => {
    // @ts-expect-error - TS2554: Expected 4 arguments, but got 2.
    let error = setFieldError(fields, formData);
    conditionalFields.map(conditionalField => {
      // @ts-expect-error - TS2339: Property 'depends_on' does not exist on type 'never'.
      if (formData[conditionalField.depends_on]) {
        const temp = setFieldError(
          // @ts-expect-error - TS2339: Property 'fields' does not exist on type 'never'.
          conditionalField.fields,
          formData,
          true,
          // @ts-expect-error - TS2339: Property 'depends_on' does not exist on type 'never'.
          conditionalField.depends_on,
        );
        error = { ...error, ...temp };
      }
    });
    return error;
  }, [formData, fields, setConditionalFields]);

  const onSubmit = async () => {
    try {
      setSubmitted(true);
      if (Object.keys(errors).length > 0) {
        return;
      }
      const temp = unflattenObject(formData);
      // @ts-expect-error - TS2339: Property 'third_party' does not exist on type '{}'.
      if (carrier?.provider?.value === 'ups' && !formData.third_party) {
        const authUrl = `${carrier.oauthUrl}&state=${encodeURI(
          // @ts-expect-error - TS2339: Property 'shipper_number' does not exist on type '{}'.
          formData.shipper_number,
        )}`;
        window.location.href = authUrl;
        return;
      }

      setError('');
      setSaving(true);
      const reqData = { credentials: temp };
      if (fulfillment_provider_id) {
        // @ts-expect-error - TS2339: Property 'fulfillment_provider_id' does not exist on type '{ credentials: {}; }'...
        reqData.fulfillment_provider_id = fulfillment_provider_id;
      }

      await ShippingLabelsCarriersService.updateShippingCredentials(
        carrier.provider?.value,
        reqData,
      );

      ToastMessage.success('Carrier Service Credentials updated successfully.');
      setSubmitted(false);
      closeModal({
        ...carrier,
        ...reqData,
      });
    } catch (error) {
      setError(error ? error.error : ErrorMessageConstant.defaultAPIError);
    } finally {
      setSaving(false);
    }
  };

  const fieldChanged = (key, value) => {
    setFormData({
      ...formData,
      [key]: value,
    });
  };

  return (
    <AppModal
      open={open}
      size="tiny"
      closeModal={() => closeModal()}
      title={`${carrier?.credentials ? 'Edit' : 'Set'} Credentials`}
    >
      <form
        noValidate
        autoComplete="off"
        onSubmit={event => {
          event.preventDefault();
          onSubmit();
        }}
        className="sk:relative"
      >
        {saving && <LoadingOverlay label="Saving" />}
        {fields.map(item =>
          field(
            // @ts-expect-error - TS2339: Property 'value' does not exist on type 'never'.
            formData[item.value] || '',
            // @ts-expect-error - TS2339: Property 'value' does not exist on type 'never'.
            submitted ? errors[item.value] : '',
            item,
            fieldChanged,
          ),
        )}
        {error && (
          <div
            role="alert"
            className="sk:mb-4 sk:w-full sk:rounded-card sk:bg-danger-bg sk:px-3 sk:py-2 sk:font-semibold sk:text-danger"
          >
            {error}
          </div>
        )}
        {conditionalFields.map(
          conditionalField =>
            // @ts-expect-error - TS2339: Property 'depends_on' does not exist on type 'never'.
            formData[conditionalField.depends_on] &&
            // @ts-expect-error - TS2339: Property 'fields' does not exist on type 'never'.
            conditionalField.fields.map(item =>
              field(
                /* @ts-expect-error - TS2339: Property 'depends_on' does not exist on type 'never'. */
                formData[`${conditionalField.depends_on}.${item.value}`] || '',
                submitted
                  ? // @ts-expect-error - TS2339: Property 'depends_on' does not exist on type 'never'.
                    errors[`${conditionalField.depends_on}.${item.value}`]
                  : '',
                item,
                fieldChanged,
                true,
                // @ts-expect-error - TS2339: Property 'depends_on' does not exist on type 'never'.
                conditionalField.depends_on,
                countryData,
                countryLoading,
              ),
            ),
        )}
        <div className="sk:flex sk:flex-wrap sk:justify-end sk:gap-2">
          <StyledButton type="submit">Submit</StyledButton>
          <HoverBorderButton type="button" onClick={() => closeModal()}>
            Cancel
          </HoverBorderButton>
        </div>
      </form>
    </AppModal>
  );
}
